import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, PackageSearch, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { getMyServiceOrders } from '@/lib/api';
import {
  apiErrorMessage,
  apiItems,
  formatCad,
  formatServiceDate,
  orderId,
  orderItems,
  orderNumber,
  orderTotalCents,
} from '@/lib/documentServices';
import { ServiceEmpty, ServiceError, ServiceLoading, ServicePageHeader, ServiceStatusBadge } from '@/components/document-services/DocumentServicesCommon';

export default function MyServiceOrders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [status, setStatus] = useState('all');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await getMyServiceOrders({ limit: 200 });
      const items = apiItems(data, ['orders']);
      setOrders(items.filter((order) => {
        if (status === 'all') return true;
        if (status === 'active') return !['completed', 'cancelled', 'refunded'].includes(order.order_status);
        if (status === 'completed') return order.order_status === 'completed' || ['delivered', 'completed'].includes(order.fulfillment_status);
        return order.order_status === 'cancelled';
      }));
    } catch (err) {
      setError(apiErrorMessage(err, 'Your orders could not be loaded.'));
    } finally {
      setLoading(false);
    }
  }, [status]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-6 animate-fade-in">
      <ServicePageHeader eyebrow="Services & Orders" title="My Orders" description="Review payment verification, document processing, pickup, delivery, and tracking in one place." actions={<Button asChild className="bg-[#062B1A] text-white hover:bg-[#041B10]"><Link to="/student/services"><Plus className="mr-2 size-4" />Browse services</Link></Button>} />
      <div className="flex flex-wrap gap-2" aria-label="Filter orders">
        {[['all', 'All'], ['active', 'Active'], ['completed', 'Completed'], ['cancelled', 'Cancelled']].map(([value, label]) => <Button key={value} type="button" variant="outline" size="sm" aria-pressed={status === value} onClick={() => setStatus(value)} className={`rounded-full ${status === value ? 'border-[#D4AF37] bg-[#062B1A] text-white hover:bg-[#041B10]' : 'border-[#062B1A]/15 text-[#062B1A]'}`}>{label}</Button>)}
      </div>
      {loading ? <ServiceLoading label="Loading your orders…" /> : error ? <ServiceError message={error} onRetry={load} /> : orders.length === 0 ? (
        <ServiceEmpty title="No orders found" description={status === 'all' ? 'When you order an MCC document, it will appear here.' : 'No orders match this filter.'} action={status === 'all' && <Button asChild className="bg-[#062B1A] text-white"><Link to="/student/services">Browse services</Link></Button>} />
      ) : (
        <div className="grid gap-4">
          {orders.map((order) => {
            const firstItem = orderItems(order)[0];
            const id = orderId(order);
            return (
              <Card key={id} className="card-hover border border-[rgba(6,43,26,0.08)] shadow-sm">
                <CardContent className="p-5">
                  <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2"><p className="font-mono text-xs font-semibold text-[#062B1A]/50">{orderNumber(order)}</p><ServiceStatusBadge kind="payment" status={order.payment_status} /><ServiceStatusBadge kind="fulfillment" status={order.fulfillment_status} /></div>
                      <h2 className="mt-2 text-lg font-semibold text-[#062B1A]">{firstItem?.product_name || order.product_name || 'Document service order'}</h2>
                      <p className="mt-1 text-sm text-[#062B1A]/50">Ordered {formatServiceDate(order.created_at)} · {formatCad(orderTotalCents(order))}</p>
                    </div>
                    <Button asChild variant="outline" className="shrink-0 border-[#062B1A]/15 text-[#062B1A]"><Link to={`/student/orders/${encodeURIComponent(id)}`}>View order <ArrowRight className="ml-2 size-4" /></Link></Button>
                  </div>
                  {order.parent_order_number && <p className="mt-4 flex items-center gap-2 rounded-lg bg-[#F7F8FA] px-3 py-2 text-xs text-[#062B1A]/55"><PackageSearch className="size-3.5" />Shipping upgrade for {order.parent_order_number}</p>}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
